import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

const CONTACT_EMAIL = "Sophiainfinityinfo@gmail.com";

const INQUIRY_TYPES = [
  "Request Private R&D Pilot Formulation Allocation",
  "Explore Institutional Licensing & Leasing Contracts",
  "Corporate Capitalization & Seed Funding Contributions",
  "Brand Partnership",
  "Open Innovation Review",
  "Donation",
  "General Information",
];

export default function ContactSheet({ trigger, defaultInquiry }) {
  const initial = defaultInquiry && INQUIRY_TYPES.includes(defaultInquiry)
    ? defaultInquiry
    : INQUIRY_TYPES[0];
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    organization: "",
    email: "",
    inquiry: initial,
    message: "",
  });

  // When sheet opens, sync inquiry with the latest defaultInquiry from caller.
  useEffect(() => {
    if (open) {
      const next = defaultInquiry && INQUIRY_TYPES.includes(defaultInquiry)
        ? defaultInquiry
        : INQUIRY_TYPES[0];
      setForm((p) => ({ ...p, inquiry: next }));
    }
  }, [open, defaultInquiry]);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `[${form.inquiry}] Secure Pitch Portal Access — ${form.name || "Anonymous"}`,
    );
    const bodyLines = [
      `Name: ${form.name}`,
      `Organization: ${form.organization || "—"}`,
      `Email: ${form.email}`,
      `Inquiry Type: ${form.inquiry}`,
      "",
      "Message:",
      form.message,
      "",
      "— Sent via Sophia Infinity Secure Pitch Portal",
    ];
    const body = encodeURIComponent(bodyLines.join("\n"));
    const mailto = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    window.location.href = mailto;
    setSubmitted(true);
  };

  const reset = () => {
    setSubmitted(false);
    const next = defaultInquiry && INQUIRY_TYPES.includes(defaultInquiry)
      ? defaultInquiry
      : INQUIRY_TYPES[0];
    setForm({
      name: "",
      organization: "",
      email: "",
      inquiry: next,
      message: "",
    });
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setTimeout(reset, 300);
      }}
    >
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="right"
        data-testid="contact-sheet"
        className="w-full sm:max-w-xl bg-[#050505] border-l border-white/10 text-white p-0 overflow-y-auto"
      >
        <div className="flex flex-col min-h-full px-8 sm:px-12 py-12">
          {/* Header */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#D4AF37]/70" />
              <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-body">
                Secure Pitch Portal
              </span>
            </div>
            <h2
              data-testid="contact-sheet-title"
              className="font-heading font-light text-3xl sm:text-4xl text-white leading-tight tracking-tight"
            >
              Request <span className="text-[#D4AF37]">access</span>.
            </h2>
            <p className="mt-4 text-sm sm:text-base text-[#E5E4E2]/70 leading-relaxed font-body max-w-md">
              Provide your details and we will route your request to the corporate development
              desk. All correspondence is handled under NDA.
            </p>
          </div>

          {submitted ? (
            <div
              data-testid="contact-sheet-success"
              className="flex-1 flex flex-col items-start justify-center border-y border-white/10 py-12"
            >
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-4">
                Transmitted
              </div>
              <h3 className="font-heading font-light text-2xl sm:text-3xl text-white mb-4">
                Your inquiry is queued.
              </h3>
              <p className="text-sm text-[#E5E4E2]/70 leading-relaxed max-w-md font-body">
                Your default mail client should now be open with your message addressed to{" "}
                <span className="text-white">{CONTACT_EMAIL}</span>. If nothing opened, please
                email us directly at the address above.
              </p>
              <SheetClose asChild>
                <button
                  data-testid="contact-sheet-close-after-submit"
                  className="mt-10 text-xs uppercase tracking-[0.3em] text-[#E5E4E2]/70 hover:text-white transition-colors duration-300 border-b border-white/20 hover:border-white pb-1"
                >
                  Close
                </button>
              </SheetClose>
            </div>
          ) : (
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="flex flex-col gap-7 flex-1"
            >
              <Field
                id="name"
                label="Full Name"
                required
                value={form.name}
                onChange={update("name")}
                testid="contact-field-name"
              />
              <Field
                id="organization"
                label="Organization (Optional)"
                value={form.organization}
                onChange={update("organization")}
                testid="contact-field-organization"
              />
              <Field
                id="email"
                label="Email Address"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                testid="contact-field-email"
              />

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="inquiry"
                  className="text-[10px] uppercase tracking-[0.3em] text-[#E5E4E2]/50 font-body"
                >
                  Nature of Inquiry
                </label>
                <select
                  id="inquiry"
                  data-testid="contact-field-inquiry"
                  value={form.inquiry}
                  onChange={update("inquiry")}
                  className="bg-transparent border-b border-white/15 focus:border-[#D4AF37] outline-none text-white font-body text-base py-3 transition-colors duration-300 appearance-none cursor-pointer"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'><path d='M2 4 L6 8 L10 4' stroke='%23D4AF37' stroke-width='1.2' fill='none'/></svg>\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 0.25rem center",
                    paddingRight: "1.5rem",
                  }}
                >
                  {INQUIRY_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-[#050505] text-white">
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="message"
                  className="text-[10px] uppercase tracking-[0.3em] text-[#E5E4E2]/50 font-body"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  data-testid="contact-field-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Briefly describe the nature and intent of your inquiry."
                  className="bg-transparent border-b border-white/15 focus:border-[#D4AF37] outline-none text-white font-body text-base py-3 transition-colors duration-300 resize-none placeholder:text-[#E5E4E2]/35"
                />
              </div>

              <p className="text-[10px] uppercase tracking-[0.3em] text-[#E5E4E2]/35 font-body">
                Correspondence routes to{" "}
                <span className="text-[#E5E4E2]/70">{CONTACT_EMAIL}</span>
              </p>

              <div className="mt-auto pt-8 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  className="group inline-flex items-center justify-center gap-3 bg-white text-black hover:bg-[#D4AF37] transition-colors duration-500 text-xs uppercase tracking-[0.3em] font-body px-8 py-4 rounded-none flex-1"
                >
                  Transmit Inquiry
                  <span
                    aria-hidden="true"
                    className="inline-block translate-x-0 group-hover:translate-x-1 transition-transform duration-500"
                  >
                    →
                  </span>
                </button>
                <SheetClose asChild>
                  <button
                    type="button"
                    data-testid="contact-form-cancel"
                    className="text-xs uppercase tracking-[0.3em] text-[#E5E4E2]/50 hover:text-white transition-colors duration-300 px-4 py-4"
                  >
                    Cancel
                  </button>
                </SheetClose>
              </div>
            </form>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ id, label, type = "text", required, value, onChange, testid }) {
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="text-[10px] uppercase tracking-[0.3em] text-[#E5E4E2]/50 font-body"
      >
        {label}
        {required ? <span className="text-[#D4AF37] ml-1">*</span> : null}
      </label>
      <input
        id={id}
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="bg-transparent border-b border-white/15 focus:border-[#D4AF37] outline-none text-white font-body text-base py-3 transition-colors duration-300 placeholder:text-[#E5E4E2]/35"
      />
    </div>
  );
}
